import { FormattedMessage } from 'react-intl';
import cx from 'clsx';
import type { ReactNode } from 'react';
import { FormattedMoney } from '@repo/design-system-kit';
import type { Section as SectionType } from '../../types';
import { usePdfPreviewContext } from '../../pdf-preview-context.tsx';
import { ItemTable } from './item-table.tsx';
import styles from './section.module.css';

interface SectionProps {
  index: number;
  section: SectionType;
  showSubtotal?: boolean;
}

export function Section({ index, section, showSubtotal }: SectionProps): ReactNode {
  return (
    <div className={styles.section} data-testid={`pdf-preview-section-${index}`}>
      {section.title ? (
        <div
          className={cx('label-1', !section.description && 'mb-8')}
          data-testid="pdf-preview-section-title"
        >
          {section.title}
        </div>
      ) : null}

      {section.description ? (
        <div
          className={cx('mb-8', 'body-1', styles.description)}
          data-testid="pdf-preview-section-description"
        >
          {section.description}
        </div>
      ) : null}

      <ItemTable items={section.items} />

      {showSubtotal ? <SectionSubtotals totalExcludingVat={section.totalExcludingVat} /> : null}
    </div>
  );
}

interface SectionSubtotalsProps {
  totalExcludingVat?: string;
}

function SectionSubtotals({ totalExcludingVat }: SectionSubtotalsProps): ReactNode {
  const { displayLanguage, currency } = usePdfPreviewContext();

  return (
    <table className={cx('mb-8', styles.totals)}>
      <tbody>
        <tr data-testid="pdf-preview-section-subtotal-excl-vat">
          <td className={cx(styles.cell, 'body-1-bold')}>
            <FormattedMessage
              id="pdf-preview.section-subtotal-base"
              values={{ language: displayLanguage }}
            />
          </td>
          <td className={cx(styles.cell, styles.amount, 'body-1')}>
            <FormattedMoney amount={totalExcludingVat} currency={currency} token="money-symbol" />
          </td>
        </tr>
      </tbody>
    </table>
  );
}
